import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    User, 
    Lock,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Eye,
    EyeOff,
    Save
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function SettingsPage() {
    const { user, token, updateUser, refreshUser } = useAuth();
    const [firstName, setFirstName] = useState(user?.first_name || '');
    const [lastName, setLastName] = useState(user?.last_name || '');
    const [savingProfile, setSavingProfile] = useState(false);
    
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const handleSaveProfile = async (e) => {
        e.preventDefault();

        if (!firstName.trim() || !lastName.trim()) {
            toast.error('Please enter your first and last name');
            return;
        }

        setSavingProfile(true);
        try {
            const response = await fetch(`${API}/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    first_name: firstName.trim(),
                    last_name: lastName.trim()
                })
            });

            if (response.ok) {
                toast.success('Profile updated successfully!');
                updateUser({ first_name: firstName.trim(), last_name: lastName.trim() });
                await refreshUser();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update profile');
            }
        } catch (error) {
            toast.error('Error updating profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API}/auth/change-password`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword
                })
            });

            if (response.ok) {
                toast.success('Password changed successfully!');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to change password');
            }
        } catch (error) {
            toast.error('Error changing password');
        } finally {
            setLoading(false);
        }
    };

    const hasProfileChanges = firstName !== user?.first_name || lastName !== user?.last_name;

    return (
        <Layout>
            <div className="max-w-2xl mx-auto" data-testid="settings-page">
                {/* Header */}
                <div className="mb-8">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-3xl lg:text-4xl font-bold text-white mb-2"
                    >
                        Settings
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-[#A3A3A3]"
                    >
                        Manage your account settings
                    </motion.p>
                </div>

                {/* Profile Info - Editable */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#262626] border border-[#404040] rounded-2xl p-6 mb-6"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <User size={20} className="text-[#F51E1E]" />
                        <h2 className="text-lg font-bold text-white">Profile Information</h2>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mb-6">
                        Update your name and personal information
                    </p>

                    <form onSubmit={handleSaveProfile} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="firstName" className="text-white">First Name</Label>
                                <Input
                                    id="firstName"
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="Enter first name"
                                    data-testid="first-name-input"
                                    className="mt-1 h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500"
                                />
                            </div>
                            <div>
                                <Label htmlFor="lastName" className="text-white">Last Name</Label>
                                <Input
                                    id="lastName"
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder="Enter last name"
                                    data-testid="last-name-input"
                                    className="mt-1 h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[#A3A3A3] text-sm">Email</Label>
                            <p className="text-white font-medium mt-1">{user?.email}</p>
                        </div>

                        <div>
                            <Label className="text-[#A3A3A3] text-sm">Role</Label>
                            <p className="text-white font-medium capitalize mt-1">{user?.role}</p>
                        </div>

                        <Button
                            type="submit"
                            disabled={savingProfile || !hasProfileChanges || !firstName.trim() || !lastName.trim()}
                            data-testid="save-profile-btn"
                            className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full font-semibold mt-4 disabled:opacity-50"
                        >
                            {savingProfile ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                <>
                                    <Save size={18} className="mr-2" />
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </form>
                </motion.div>

                {/* Change Password */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-[#262626] border border-[#404040] rounded-2xl p-6"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Lock size={20} className="text-[#F51E1E]" />
                        <h2 className="text-lg font-bold text-white">Change Password</h2>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mb-6">
                        Update your password to keep your account secure
                    </p>

                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div>
                            <Label htmlFor="currentPassword" className="text-white">
                                Current Password
                            </Label>
                            <div className="relative mt-1">
                                <Input
                                    id="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="Enter current password"
                                    data-testid="current-password-input"
                                    className="h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500 pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                                >
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="newPassword" className="text-white">
                                New Password
                            </Label>
                            <div className="relative mt-1">
                                <Input
                                    id="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    data-testid="new-password-input"
                                    className="h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500 pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                                >
                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <p className="text-xs text-[#666] mt-1">At least 6 characters</p>
                        </div>

                        <div>
                            <Label htmlFor="confirmPassword" className="text-white">
                                Confirm New Password
                            </Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                data-testid="confirm-password-input"
                                className="mt-1 h-11 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500"
                            />
                        </div>

                        {newPassword && confirmPassword && (
                            <div className={`flex items-center gap-2 text-sm ${
                                newPassword === confirmPassword ? 'text-green-400' : 'text-red-400'
                            }`}>
                                {newPassword === confirmPassword ? (
                                    <>
                                        <CheckCircle2 size={16} />
                                        <span>Passwords match</span>
                                    </>
                                ) : (
                                    <>
                                        <AlertCircle size={16} />
                                        <span>Passwords do not match</span>
                                    </>
                                )}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                            data-testid="change-password-btn"
                            className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full font-semibold mt-6 disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                'Change Password'
                            )}
                        </Button>
                    </form>
                </motion.div>
            </div>
        </Layout>
    );
}
